import Peer, { type DataConnection } from 'peerjs';
import type { MoveAction, Side } from '../game/world';
import { loadPeerConfig } from './config';
import { isClientMessage, isServerMessage, MAX_PLAYERS, PROTOCOL_VERSION, type GameSnapshot, type ServerMessage } from './protocol';

export type SessionEvent =
  | { readonly type: 'status'; readonly text: string }
  | { readonly type: 'welcome'; readonly playerId: string; readonly side: Side }
  | { readonly type: 'input'; readonly playerId: string; readonly move: MoveAction }
  | { readonly type: 'restart' | 'start' }
  | ({ readonly type: 'snapshot' } & GameSnapshot)
  | { readonly type: 'leave'; readonly playerId: string };

export class PeerSession {
  #peer: Peer | undefined;
  #hostConnection: DataConnection | undefined;
  readonly #clients = new Map<string, DataConnection>();
  readonly #lastSequence = new Map<string, number>();
  #listener: (event: SessionEvent) => void = () => undefined;

  onEvent(listener: (event: SessionEvent) => void): void { this.#listener = listener; }

  async create(): Promise<string> {
    this.close();
    const peer = this.#createPeer();
    this.#peer = peer;
    const id = await this.#waitForOpen(peer);
    peer.on('connection', (connection) => this.#acceptClient(connection));
    this.#listener({ type: 'status', text: `Room ready: ${id}` });
    return id;
  }

  async join(hostId: string): Promise<void> {
    this.close();
    const peer = this.#createPeer();
    this.#peer = peer;
    await this.#waitForOpen(peer);
    const connection = peer.connect(hostId, { reliable: true, serialization: 'json' });
    this.#hostConnection = connection;
    await new Promise<void>((resolve, reject) => {
      connection.once('open', resolve);
      connection.once('error', reject);
    });
    connection.on('data', (data) => {
      if (!isServerMessage(data)) return;
      if (data.type === 'snapshot') this.#listener({ type: 'snapshot', tick: data.tick, hitCount: data.hitCount, started: data.started, paddles: data.paddles, ball: data.ball, winner: data.winner });
      if (data.type === 'welcome') {
        this.#listener({ type: 'welcome', playerId: data.playerId, side: data.side });
        this.#listener({ type: 'status', text: 'Player 2 joined — waiting for Start' });
      }
      if (data.type === 'reject') this.#listener({ type: 'status', text: `Rejected: ${data.reason}` });
    });
    connection.on('close', () => this.#listener({ type: 'status', text: 'Host disconnected — create or join another room' }));
  }

  sendInput(sequence: number, move: MoveAction): void {
    if (this.#hostConnection?.open) this.#hostConnection.send({ version: PROTOCOL_VERSION, type: 'input', sequence, move });
  }

  sendRestart(): void {
    if (this.#hostConnection?.open) this.#hostConnection.send({ version: PROTOCOL_VERSION, type: 'restart' });
  }

  sendStart(): void {
    if (this.#hostConnection?.open) this.#hostConnection.send({ version: PROTOCOL_VERSION, type: 'start' });
  }

  broadcastSnapshot(snapshot: GameSnapshot): void {
    const message: ServerMessage = { version: PROTOCOL_VERSION, type: 'snapshot', ...snapshot };
    for (const connection of this.#clients.values()) if (connection.open) connection.send(message);
  }

  close(): void {
    this.#hostConnection?.close();
    for (const connection of this.#clients.values()) connection.close();
    this.#peer?.destroy();
    this.#hostConnection = undefined;
    this.#peer = undefined;
    this.#clients.clear();
    this.#lastSequence.clear();
  }

  #createPeer(): Peer {
    const config = loadPeerConfig(import.meta.env);
    const peer = new Peer({ ...config, config: { iceServers: config.iceServers } });
    peer.on('error', (error) => this.#listener({ type: 'status', text: `Connection failed: ${error.type}` }));
    return peer;
  }

  #waitForOpen(peer: Peer): Promise<string> {
    return new Promise((resolve, reject) => {
      peer.once('open', resolve);
      peer.once('error', reject);
    });
  }

  #acceptClient(connection: DataConnection): void {
    if (this.#clients.size >= MAX_PLAYERS - 1) {
      connection.on('open', () => {
        connection.send({ version: PROTOCOL_VERSION, type: 'reject', reason: 'Room full' } satisfies ServerMessage);
        connection.close();
      });
      return;
    }
    connection.on('open', () => {
      this.#clients.set(connection.peer, connection);
      connection.send({ version: PROTOCOL_VERSION, type: 'welcome', playerId: connection.peer, side: 'north' } satisfies ServerMessage);
      this.#listener({ type: 'welcome', playerId: connection.peer, side: 'north' });
      this.#listener({ type: 'input', playerId: connection.peer, move: { x: 0 } });
    });
    connection.on('data', (data) => {
      if (!isClientMessage(data)) return;
      if (data.type === 'restart' || data.type === 'start') {
        this.#listener({ type: data.type });
        return;
      }
      if (data.type !== 'input') return;
      const previous = this.#lastSequence.get(connection.peer) ?? -1;
      if (data.sequence <= previous) return;
      this.#lastSequence.set(connection.peer, data.sequence);
      this.#listener({ type: 'input', playerId: connection.peer, move: data.move });
    });
    connection.on('close', () => {
      this.#clients.delete(connection.peer);
      this.#lastSequence.delete(connection.peer);
      this.#listener({ type: 'leave', playerId: connection.peer });
    });
  }
}
